import { type HTMLAttributes, type ThHTMLAttributes, type TdHTMLAttributes, forwardRef } from 'react';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SortDir } from '@/lib/useTableSort';

export const Table = forwardRef<HTMLTableElement, HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <div className="relative w-full overflow-auto rounded-md border border-border scrollbar-thin">
      <table ref={ref} className={cn('w-full caption-bottom text-sm', className)} {...props} />
    </div>
  ),
);
Table.displayName = 'Table';

export const THead = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <thead ref={ref} className={cn('bg-muted/40 [&_tr]:border-b', className)} {...props} />
  ),
);
THead.displayName = 'THead';

export const TBody = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <tbody ref={ref} className={cn('[&_tr:last-child]:border-0', className)} {...props} />
  ),
);
TBody.displayName = 'TBody';

export const TR = forwardRef<HTMLTableRowElement, HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr ref={ref} className={cn('border-b border-border transition-colors hover:bg-muted/30', className)} {...props} />
  ),
);
TR.displayName = 'TR';

export const TH = forwardRef<HTMLTableCellElement, ThHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        'h-10 px-3 text-left align-middle text-xs font-semibold uppercase tracking-wide text-muted-foreground',
        className,
      )}
      {...props}
    />
  ),
);
TH.displayName = 'TH';

export const TD = forwardRef<HTMLTableCellElement, TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <td ref={ref} className={cn('px-3 py-2 align-middle', className)} {...props} />
  ),
);
TD.displayName = 'TD';

export interface SortableTHProps {
  label: string;
  sortKey: string;
  /** True when this column is the one currently driving the sort. */
  active: boolean;
  /** Active direction. `null` means sortable-but-inactive (shows the neutral ↕). */
  direction: SortDir;
  onSort: (key: string) => void;
  align?: 'left' | 'right';
  className?: string;
}

/**
 * A clickable, keyboard-accessible column header that drives `useTableSort`
 * (client-side) or a server-side `.order()` state. Renders the label plus a
 * sort indicator: an up arrow when actively ascending, a down arrow when
 * actively descending, and a muted ↕ (visible on hover/focus) when the column
 * is sortable but inactive. Emits `aria-sort` for assistive tech.
 */
export const SortableTH = forwardRef<HTMLTableCellElement, SortableTHProps>(
  ({ label, sortKey, active, direction, onSort, align = 'left', className }, ref) => {
    const ariaSort = active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none';
    return (
      <th
        ref={ref}
        aria-sort={ariaSort}
        className={cn(
          'h-10 p-0 align-middle text-xs font-semibold uppercase tracking-wide text-muted-foreground',
          className,
        )}
      >
        <button
          type="button"
          onClick={() => onSort(sortKey)}
          className={cn(
            'group flex h-10 w-full select-none items-center gap-1 px-3 transition-colors hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none cursor-pointer',
            align === 'right' ? 'justify-end' : 'justify-start',
          )}
        >
          <span>{label}</span>
          <SortIndicator active={active} direction={direction} />
        </button>
      </th>
    );
  },
);
SortableTH.displayName = 'SortableTH';

function SortIndicator({ active, direction }: { active: boolean; direction: SortDir }) {
  if (active && direction === 'asc') {
    return <ArrowUp className="h-3 w-3 shrink-0 text-foreground" aria-hidden />;
  }
  if (active && direction === 'desc') {
    return <ArrowDown className="h-3 w-3 shrink-0 text-foreground" aria-hidden />;
  }
  // Sortable but inactive: muted, and only fully visible on hover/focus.
  return (
    <ChevronsUpDown
      className="h-3 w-3 shrink-0 text-muted-foreground/40 transition-opacity group-hover:text-muted-foreground group-focus-visible:text-muted-foreground"
      aria-hidden
    />
  );
}
